import type { PodcastItem } from '@/types';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchStorageAsset } from '@/features/search/searchSlice';
import { setTranscriptSearch } from '@/features/ui/uiSlice';
import { setPlaying, updateCurrentTime, requestAutoplay } from '@/features/player/playerSlice';
import { useHoverVideoPreview } from '@/hooks/useHoverVideoPreview';
import { trackEvent } from '@/services/analyticsService';
import { ANALYTICS_EVENTS } from '@/config/analyticsEvents';
import demoVideo from '@/assets/videos/Image_from_iOS.MOV';
import styles from './PodcastTile.module.css';

interface PodcastTileProps {
  item: PodcastItem;
  variant?: 'grid';
}

export default function PodcastTile({ item, variant }: PodcastTileProps) {
  const dispatch = useAppDispatch();
  const { activeAsset, loadingAssetId } = useAppSelector((s) => s.search);

  // Use real storage_url when available; hook handles lazy loading + 5 s loop
  const { videoRef, handleMouseEnter, handleMouseLeave } = useHoverVideoPreview(
    item.storage_url
  );

  const isLoading = loadingAssetId === item.id;
  const isActive = activeAsset?.asset_id === item.id && !loadingAssetId;

  const handleClick = () => {
    trackEvent(ANALYTICS_EVENTS.MEDIA_TILE_PLAY, { assetId: item.id, tileType: 'podcast' });
    dispatch(setPlaying(false));
    dispatch(updateCurrentTime(0));
    dispatch(requestAutoplay());
    dispatch(setTranscriptSearch(''));
    dispatch(fetchStorageAsset(item.id));
    // Scroll hero section into view smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div
      className={`${styles.tile} ${isActive && variant !== 'grid' ? styles.tileActive : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-label={`Play ${item.title}`}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      <div className={styles.thumb}>
        {item.thumbnail ? (
          <img src={item.thumbnail} className={styles.thumbImg} alt={item.title} />
        ) : (
          <>
            <div className={styles.thumbPlaceholder} />
            <div className={styles.thumbIcon} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="44" height="44">
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="22" />
              </svg>
            </div>
          </>
        )}
        <video
          ref={videoRef}
          className={styles.thumbVideo}
          src={item.storage_url ? undefined : demoVideo}
          muted
          loop={!item.storage_url}
          playsInline
          preload="none"
        />
        <div className={styles.podMark} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="22" />
          </svg>
        </div>
        <span className={styles.speaker}>{item.speaker}</span>
        {item.duration && (
          <span className={styles.duration}>{item.duration}</span>
        )}

        {variant === 'grid' && (
          <div className={styles.thumbTitleOverlay}>
            <span className={styles.thumbTitleText}>{item.title}</span>
          </div>
        )}

        {/* Loading spinner overlay */}
        {isLoading && (
          <div className={styles.loadingOverlay} aria-label="Loading">
            <div className={styles.spinner} />
          </div>
        )}

        {/* Active checkmark overlay */}
        {isActive && variant !== 'grid' && (
          <div className={styles.activeOverlay} aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
        )}
      </div>

      {variant !== 'grid' && <div className={styles.info}>
        <h5 className={styles.infoTitle}>{item.title}</h5>
        <div className={styles.infoMeta}>{item.meta}</div>
        <div className={styles.tags}>
          {item.tags.map((tag) => (
            <span key={tag} className={styles.tag}>
              {tag}
            </span>
          ))}
        </div>
      </div>}
    </div>
  );
}

